// Global, server-synced settings controlling automatic face-detection behavior (see
// media-lightbox.ts "magic wand" pending-faces feature and the Face recognition tab in
// views/settings.ts):
//  - the margin-expansion percentages that turn YuNet's tight, eyebrows-to-chin detection box
//    into something closer to a full head (see PersonGrpcService.DetectFaces/ApplyMargins on the
//    backend, which actually applies them)
//  - the on-screen face size (in pixels) below which a pending face gets a magnified crop preview
// Cached in localStorage for synchronous access, with the canonical values synced to the server
// via the settings view.

export const FACE_MARGIN_TOP_KEY = 'radish-face-margin-top-percent';
export const FACE_MARGIN_SIDE_KEY = 'radish-face-margin-side-percent';
export const FACE_MARGIN_BOTTOM_KEY = 'radish-face-margin-bottom-percent';
export const FACE_ZOOM_THRESHOLD_KEY = 'radish-face-zoom-threshold-px';
export const FACE_SCORE_THRESHOLD_KEY = 'radish-face-score-threshold';
export const FACE_MAX_WORKING_DIMENSION_KEY = 'radish-face-max-working-dimension';

export const DEFAULT_FACE_MARGIN_TOP_PERCENT = 40;
export const DEFAULT_FACE_MARGIN_SIDE_PERCENT = 15;
export const DEFAULT_FACE_MARGIN_BOTTOM_PERCENT = 15;
export const DEFAULT_FACE_ZOOM_THRESHOLD_PX = 80;
// Mirrors FaceDetectionService.DefaultScoreThreshold/DefaultMaxWorkingDimension on the backend —
// lowering the score threshold and/or raising the working dimension both increase recall (catch
// more/smaller faces) at the cost of more CPU time per detection run, so both are user-tunable to
// fit the available hardware (e.g. a Raspberry Pi vs. a desktop).
export const DEFAULT_FACE_SCORE_THRESHOLD = 0.6;
export const MIN_FACE_SCORE_THRESHOLD = 0.05;
export const MAX_FACE_SCORE_THRESHOLD = 0.95;
export const DEFAULT_FACE_MAX_WORKING_DIMENSION = 1600;
export const MIN_FACE_MAX_WORKING_DIMENSION = 320;
export const MAX_FACE_MAX_WORKING_DIMENSION = 4000;

function loadNumber(key: string, defaultValue: number, min: number): number {
  const stored = Number(localStorage.getItem(key));
  return Number.isFinite(stored) && stored >= min ? stored : defaultValue;
}

let _faceMarginTopPercent = loadNumber(FACE_MARGIN_TOP_KEY, DEFAULT_FACE_MARGIN_TOP_PERCENT, 0);
let _faceMarginSidePercent = loadNumber(FACE_MARGIN_SIDE_KEY, DEFAULT_FACE_MARGIN_SIDE_PERCENT, 0);
let _faceMarginBottomPercent = loadNumber(FACE_MARGIN_BOTTOM_KEY, DEFAULT_FACE_MARGIN_BOTTOM_PERCENT, 0);
let _faceZoomThresholdPx = loadNumber(FACE_ZOOM_THRESHOLD_KEY, DEFAULT_FACE_ZOOM_THRESHOLD_PX, 1);
let _faceScoreThreshold = loadNumber(FACE_SCORE_THRESHOLD_KEY, DEFAULT_FACE_SCORE_THRESHOLD, MIN_FACE_SCORE_THRESHOLD);
let _faceMaxWorkingDimension = loadNumber(
  FACE_MAX_WORKING_DIMENSION_KEY,
  DEFAULT_FACE_MAX_WORKING_DIMENSION,
  MIN_FACE_MAX_WORKING_DIMENSION,
);

export function getFaceMarginTopPercent(): number {
  return _faceMarginTopPercent;
}

export function setFaceMarginTopPercent(value: number): void {
  _faceMarginTopPercent = Number.isFinite(value) && value >= 0 ? value : DEFAULT_FACE_MARGIN_TOP_PERCENT;
  localStorage.setItem(FACE_MARGIN_TOP_KEY, String(_faceMarginTopPercent));
}

export function getFaceMarginSidePercent(): number {
  return _faceMarginSidePercent;
}

export function setFaceMarginSidePercent(value: number): void {
  _faceMarginSidePercent = Number.isFinite(value) && value >= 0 ? value : DEFAULT_FACE_MARGIN_SIDE_PERCENT;
  localStorage.setItem(FACE_MARGIN_SIDE_KEY, String(_faceMarginSidePercent));
}

export function getFaceMarginBottomPercent(): number {
  return _faceMarginBottomPercent;
}

export function setFaceMarginBottomPercent(value: number): void {
  _faceMarginBottomPercent = Number.isFinite(value) && value >= 0 ? value : DEFAULT_FACE_MARGIN_BOTTOM_PERCENT;
  localStorage.setItem(FACE_MARGIN_BOTTOM_KEY, String(_faceMarginBottomPercent));
}

export function getFaceZoomThresholdPx(): number {
  return _faceZoomThresholdPx;
}

export function setFaceZoomThresholdPx(value: number): void {
  _faceZoomThresholdPx = Number.isFinite(value) && value > 0 ? Math.round(value) : DEFAULT_FACE_ZOOM_THRESHOLD_PX;
  localStorage.setItem(FACE_ZOOM_THRESHOLD_KEY, String(_faceZoomThresholdPx));
}

export function getFaceScoreThreshold(): number {
  return _faceScoreThreshold;
}

export function setFaceScoreThreshold(value: number): void {
  _faceScoreThreshold =
    Number.isFinite(value) && value >= MIN_FACE_SCORE_THRESHOLD && value <= MAX_FACE_SCORE_THRESHOLD
      ? value
      : DEFAULT_FACE_SCORE_THRESHOLD;
  localStorage.setItem(FACE_SCORE_THRESHOLD_KEY, String(_faceScoreThreshold));
}

export function getFaceMaxWorkingDimension(): number {
  return _faceMaxWorkingDimension;
}

export function setFaceMaxWorkingDimension(value: number): void {
  _faceMaxWorkingDimension =
    Number.isFinite(value) && value >= MIN_FACE_MAX_WORKING_DIMENSION && value <= MAX_FACE_MAX_WORKING_DIMENSION
      ? Math.round(value)
      : DEFAULT_FACE_MAX_WORKING_DIMENSION;
  localStorage.setItem(FACE_MAX_WORKING_DIMENSION_KEY, String(_faceMaxWorkingDimension));
}
